import { Paper, Box, Typography, Alert, Button, Grid, Card, CardContent, Stack, Divider, alpha } from '@mui/material';
import { ConfirmationNumber, Event, Directions, CalendarMonth, LocationOn } from '@mui/icons-material';
import { ILocationList } from '../../../interfaces/ILocationList';
import { formatDateRange } from '../../../utils/formatters';
import { IPublicEventDetails } from '../../../interfaces/IPublicEventDetails';

interface TicketsAvailableSummaryProps {
    event: IPublicEventDetails;
    locations: ILocationList[];
    isWaitlistRegistration?: boolean;
    onRegister: () => void;
    onAssignTickets: () => void;
    onAddToCalendar: () => void;
    onBuyMoreTickets: () => void;
    onGetDirections: (locationId: string) => void;
}

const TicketsAvailableSummary: React.FC<TicketsAvailableSummaryProps> = ({
    event,
    locations,
    isWaitlistRegistration = false,
    onRegister,
    onAssignTickets,
    onAddToCalendar,
    onBuyMoreTickets,
    onGetDirections,
}) => (
    <Paper
        elevation={3}
        sx={{
            p: 4,
            borderRadius: 3,
            mb: 4,
            border: `1px solid ${alpha('#493193', 0.1)}`,
            boxShadow: `0 8px 32px ${alpha('#493193', 0.08)}`,
        }}
    >
        <Alert
            severity="info"
            sx={{
                mb: 3,
                borderRadius: 2,
                border: `1px solid ${alpha('#0288d1', 0.3)}`,
            }}
        >
            <Typography
                variant="h5"
                sx={{
                    fontWeight: 700,
                    mb: 1,
                    background: `linear-gradient(135deg, #493193 0%, #2fadd8 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                }}
            >
                {isWaitlistRegistration ? 'Your Spot is Ready!' : 'Tickets Available'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {isWaitlistRegistration
                    ? 'Tickets are now available for your waitlist registration. Register now to confirm your spot before they run out.'
                    : 'You have tickets available to assign. Assign them to yourself or others to complete registration.'}
            </Typography>
            <Button
                variant="contained"
                onClick={isWaitlistRegistration ? onRegister : onAssignTickets}
                sx={{
                    borderRadius: '50px',
                    bgcolor: '#493193',
                    color: 'white',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    boxShadow: '0 4px 14px rgba(73, 49, 147, 0.4)',
                    '&:hover': {
                        bgcolor: '#3a2676',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(73, 49, 147, 0.5)',
                    },
                }}
            >
                {isWaitlistRegistration ? 'Register now' : 'Assign tickets'}
            </Button>
        </Alert>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                    variant="outlined"
                    sx={{
                        height: '100%',
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            borderColor: alpha('#493193', 0.4),
                            boxShadow: `0 4px 12px ${alpha('#493193', 0.15)}`,
                            transform: 'translateY(-2px)',
                        },
                    }}
                >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Event sx={{ fontSize: 32, mb: 1, color: '#493193' }} />
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                            Add to Calendar
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={onAddToCalendar}
                            fullWidth
                            sx={{
                                borderRadius: '50px',
                                borderColor: alpha('#493193', 0.3),
                                color: '#493193',
                                fontWeight: 600,
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: '#493193',
                                    bgcolor: alpha('#493193', 0.05),
                                },
                            }}
                        >
                            Add Event
                        </Button>
                    </CardContent>
                </Card>
            </Grid>
            {locations.length > 0 && (
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                        variant="outlined"
                        sx={{
                            height: '100%',
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                borderColor: alpha('#493193', 0.4),
                                boxShadow: `0 4px 12px ${alpha('#493193', 0.15)}`,
                                transform: 'translateY(-2px)',
                            },
                        }}
                    >
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Directions sx={{ fontSize: 32, mb: 1, color: '#493193' }} />
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                {locations.length === 1 ? 'Get Directions' : 'Plan Route'}
                            </Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onGetDirections(locations[0].id)}
                                fullWidth
                                sx={{
                                    borderRadius: '50px',
                                    borderColor: alpha('#493193', 0.3),
                                    color: '#493193',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        borderColor: '#493193',
                                        bgcolor: alpha('#493193', 0.05),
                                    },
                                }}
                            >
                                {locations.length === 1 ? 'Directions' : 'Plan Route'}
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            )}
            {!isWaitlistRegistration && (
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                        variant="outlined"
                        sx={{
                            height: '100%',
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                borderColor: alpha('#493193', 0.4),
                                boxShadow: `0 4px 12px ${alpha('#493193', 0.15)}`,
                                transform: 'translateY(-2px)',
                            },
                        }}
                    >
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <ConfirmationNumber sx={{ fontSize: 32, mb: 1, color: '#493193' }} />
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                More Tickets
                            </Typography>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={onBuyMoreTickets}
                                fullWidth
                                sx={{
                                    borderRadius: '50px',
                                    bgcolor: '#493193',
                                    color: 'white',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    boxShadow: '0 4px 14px rgba(73, 49, 147, 0.4)',
                                    '&:hover': {
                                        bgcolor: '#3a2676',
                                        boxShadow: '0 6px 20px rgba(73, 49, 147, 0.5)',
                                    },
                                }}
                            >
                                Buy More
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            )}
        </Grid>
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Event Summary
            </Typography>
            <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarMonth fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                        {formatDateRange(event.startDate, event.endDate, event.isSingleDay)}
                    </Typography>
                </Box>
                {locations.length > 0 && (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <LocationOn fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {locations.length === 1 ? 'Location' : `${locations.length} Locations`}
                            </Typography>
                        </Box>
                        <Stack spacing={1.5} sx={{ ml: 3 }}>
                            {locations.map((location) => (
                                <Box key={location.id} sx={{
                                    p: 1.5,
                                    bgcolor: 'background.paper',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                {location.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                {location.streetAddress}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                {location.city}, {location.region} {location.postalCode}
                                            </Typography>
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => onGetDirections(location.id)}
                                            sx={{
                                                ml: 1,
                                                minWidth: 'auto',
                                                px: 2,
                                                borderRadius: '50px',
                                                fontSize: '0.75rem',
                                                borderColor: alpha('#493193', 0.3),
                                                color: '#493193',
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    borderColor: '#493193',
                                                    bgcolor: alpha('#493193', 0.05),
                                                },
                                            }}
                                        >
                                            Directions
                                        </Button>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}
            </Stack>
        </Box>
    </Paper>
);

export default TicketsAvailableSummary;